import FixaButton from "@coworker/apprestructured/src/shared/wrappers/FixaButton/FixaButton";
import { FixaListViewItem } from "@coworker/apprestructured/src/shared/wrappers/FixaListView/FixaListViewItem";
import { FixaModal } from "@coworker/apprestructured/src/shared/wrappers/FixaModal/FixaModal";
import { FixaModalFooter } from "@coworker/apprestructured/src/shared/wrappers/FixaModal/FixaModalFooter";
import { FixaModalHeader } from "@coworker/apprestructured/src/shared/wrappers/FixaModal/FixaModalHeader";
import { FixaSheets } from "@coworker/apprestructured/src/shared/wrappers/FixaModal/FixaSheets";
import { useTranslation } from "@coworker/locales";
import { useCallback, useMemo, useState } from "react";
import { FixaModalBody } from "@coworker/apprestructured/src/shared/wrappers/FixaModal/FixaModalBody";
import useSaveGuardedFunction from "@coworker/apprestructured/src/save-guard/useSaveGuardedFunction";
import { FixaTextArea } from "@coworker/apprestructured/src/shared/wrappers/FixaTextArea/FixaTextArea";
import { FixaFormField } from "@coworker/apprestructured/src/shared/wrappers/FixaFormField/FixaFormField";
import styles from "./SelectItemWithDescription.module.css";
import { FixaListView } from "@coworker/apprestructured/src/shared/wrappers/FixaListView/FixaListView";

export type ItemWithDescription = {
  label: string;
  value: string;
  requireDescription: boolean;
};

type SelectItemWithDescriptionProps = {
  items: ItemWithDescription[];
  value: string;
  description: string;
  onConfirmClick: (newValue: string, newDescription: string) => void;
  disabled: boolean;
  title: string;
};

function SelectItemWithDescription({
  items,
  value,
  description,
  onConfirmClick,
  disabled,
  title,
}: SelectItemWithDescriptionProps) {
  const [showModal, setShowModal] = useState(false);
  const [newValue, setNewValue] = useState<string>(value);
  const [newDescription, setNewDescription] = useState<string>(description);
  const [shouldValidate, setShouldValidate] = useState(false);

  const { t } = useTranslation();

  const selectedItem = useMemo(
    () => items.find((item) => item.value === newValue),
    [items, newValue]
  );

  const requiredDescription = selectedItem?.requireDescription ?? false;
  const validDescription =
    !requiredDescription || newDescription.trim().length > 0;

  const hasError = !validDescription;

  const handleOpenModal = useCallback(() => {
    setNewValue(value);
    setNewDescription(description);
    setShowModal(true);
    setShouldValidate(false);
  }, [value, description]);

  const {
    saveGuardedFunction: handleCloseModal,
    enableSaveGuard,
    disableSaveGuard,
  } = useSaveGuardedFunction(
    useCallback(() => {
      setShowModal(false);
      setShouldValidate(false);
    }, [])
  );

  const handleConfirm = useCallback(() => {
    if (hasError) {
      setShouldValidate(true);
      return;
    }

    onConfirmClick(newValue, newDescription);
    setShowModal(false);
    setShouldValidate(false);
    disableSaveGuard();
  }, [hasError, onConfirmClick, newValue, newDescription, disableSaveGuard]);

  const handleDescriptionChange = useCallback(
    (e: React.ChangeEvent<HTMLTextAreaElement>) => {
      setNewDescription(e.target.value);
      enableSaveGuard();
    },
    [enableSaveGuard]
  );

  const itemLabel = useMemo(() => {
    return items.find((item) => item.value === value)?.label || "";
  }, [items, value]);

  const handleItemChange = useCallback(
    (nextValue: string) => {
      setNewValue(nextValue);
      setNewDescription("");
      enableSaveGuard();
    },
    [enableSaveGuard]
  );

  return (
    <>
      <FixaListViewItem
        title={title}
        inset
        onClick={handleOpenModal}
        emphasised={!!itemLabel}
        description={itemLabel}
        control="navigational"
        controlIcon={null}
        disabled={disabled}
      />
      <FixaModal
        visible={showModal}
        handleCloseBtn={handleCloseModal}
        data-testid="SelectItemWithDescription-FixaModal"
      >
        <FixaSheets
          header={
            <FixaModalHeader title={title} closeBtnClick={handleCloseModal} />
          }
          footer={
            <FixaModalFooter renderBorder>
              <FixaButton
                type="primary"
                fluid
                text={t("confirmString")}
                onClick={handleConfirm}
              />
            </FixaModalFooter>
          }
          fullSize
        >
          <FixaModalBody>
            <FixaListView id="SelectItemWithDescription-ListView">
              {items.map((item) => {
                const isSelected = newValue === item.value;

                return (
                  <div key={item.value}>
                    <FixaListViewItem
                      title={item.label}
                      onChange={() => handleItemChange(item.value)}
                      control="radiobutton"
                      controlProps={{
                        checked: isSelected,
                        value: item.value,
                      }}
                      disabled={disabled}
                    />

                    {isSelected && (
                      <FixaFormField
                        className={styles["formField"]}
                        valid={validDescription}
                        validation={{
                          type: "error",
                          msg: t("pleaseFillInString"),
                          id: "descriptionValidation",
                        }}
                        shouldValidate={
                          shouldValidate && item.requireDescription
                        }
                      >
                        <FixaTextArea
                          label={t("description4String")}
                          value={newDescription}
                          onChange={handleDescriptionChange}
                        />
                      </FixaFormField>
                    )}
                  </div>
                );
              })}
            </FixaListView>
          </FixaModalBody>
        </FixaSheets>
      </FixaModal>
    </>
  );
}

export default SelectItemWithDescription;
